import { useState, useEffect, useCallback } from "react";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock,
  User, Car, Wrench, Loader2, AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colors for work orders - different colors for each bridge
const BRIDGE_COLORS = [
  { bg: "bg-blue-500/80", border: "border-blue-400", text: "text-blue-100" },
  { bg: "bg-green-500/80", border: "border-green-400", text: "text-green-100" },
  { bg: "bg-purple-500/80", border: "border-purple-400", text: "text-purple-100" },
  { bg: "bg-orange-500/80", border: "border-orange-400", text: "text-orange-100" },
  { bg: "bg-pink-500/80", border: "border-pink-400", text: "text-pink-100" },
  { bg: "bg-cyan-500/80", border: "border-cyan-400", text: "text-cyan-100" },
  { bg: "bg-yellow-500/80", border: "border-yellow-400", text: "text-yellow-100" },
  { bg: "bg-red-500/80", border: "border-red-400", text: "text-red-100" },
];

const URGENCY_INDICATOR = {
  high: "border-l-4 border-l-red-500",
  medium: "border-l-4 border-l-yellow-500",
  low: "border-l-4 border-l-green-500",
};

const DAYS_IT = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
const MONTHS_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", 
                   "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export default function WorkSchedule() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split("T")[0];
      const res = await authFetch(`${API}/work-orders/schedule?date=${dateStr}`, { 
      });
      if (res.ok) {
        const data = await res.json();
        setSchedule(data);
      }
    } catch (error) {
      console.error("Error fetching schedule:", error);
      toast.error("Errore nel caricamento dell'agenda");
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  // Navigate dates
  const goToPrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Format date for display
  const formatDateDisplay = () => {
    const day = DAYS_IT[selectedDate.getDay()];
    const date = selectedDate.getDate();
    const month = MONTHS_IT[selectedDate.getMonth()];
    const year = selectedDate.getFullYear();
    return `${day} ${date} ${month} ${year}`;
  };

  // Check if selected date is today
  const isToday = () => {
    const today = new Date();
    return selectedDate.toDateString() === today.toDateString();
  };

  // Generate time slots for the day (every 30 minutes)
  const generateTimeSlots = () => {
    if (!schedule?.working_periods?.length) return [];
    
    const slots = [];
    const [startPeriod] = schedule.working_periods;
    const lastPeriod = schedule.working_periods[schedule.working_periods.length - 1];
    
    const [startH] = startPeriod[0].split(":").map(Number);
    const [endH] = lastPeriod[1].split(":").map(Number);
    
    for (let h = startH; h <= endH; h++) {
      slots.push(`${h.toString().padStart(2, "0")}:00`);
      if (h < endH) {
        slots.push(`${h.toString().padStart(2, "0")}:30`);
      }
    }
    
    return slots;
  };

  // Calculate position and height of work order block
  const calculateBlockStyle = (order) => {
    if (!schedule?.working_periods?.length) return {};
    
    const [startPeriod] = schedule.working_periods;
    const lastPeriod = schedule.working_periods[schedule.working_periods.length - 1];
    
    // Calculate total visual span (from first period start to last period end)
    const [visualStartH, visualStartM] = startPeriod[0].split(":").map(Number);
    const [visualEndH, visualEndM] = lastPeriod[1].split(":").map(Number);
    const visualStartMinutes = visualStartH * 60 + visualStartM;
    const visualEndMinutes = visualEndH * 60 + visualEndM;
    const totalVisualMinutes = visualEndMinutes - visualStartMinutes;
    
    const [orderStartH, orderStartM] = (order.scheduled_time_start || "08:00").split(":").map(Number);
    const [orderEndH, orderEndM] = (order.scheduled_time_end || "18:00").split(":").map(Number);
    const orderStartMinutes = orderStartH * 60 + orderStartM;
    const orderEndMinutes = orderEndH * 60 + orderEndM;
    
    // Position based on visual timeline (including break time visually)
    const top = ((orderStartMinutes - visualStartMinutes) / totalVisualMinutes) * 100;
    const height = ((orderEndMinutes - orderStartMinutes) / totalVisualMinutes) * 100;
    
    return {
      top: `${Math.max(0, top)}%`,
      height: `${Math.min(100 - top, Math.max(height, 5))}%`,
      minHeight: "40px"
    };
  };

  // Get color for bridge
  const getBridgeColor = (bridgeNum) => {
    return BRIDGE_COLORS[(bridgeNum - 1) % BRIDGE_COLORS.length];
  };

  const timeSlots = generateTimeSlots();

  if (loading && !schedule) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="work-schedule-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <CalendarIcon className="w-7 h-7 text-primary" />
            Agenda Lavori
          </h1>
          <p className="text-zinc-400 mt-1">
            Visualizzazione giornaliera degli ordini di lavoro schedulati
          </p>
        </div>
      </div>

      {/* Date Navigation */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={goToPrevDay}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Giorno Precedente
          </Button>
          
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-white">
              {formatDateDisplay()}
            </h2>
            {!isToday() && (
              <Button variant="outline" size="sm" onClick={goToToday}>
                Oggi
              </Button>
            )}
          </div>
          
          <Button variant="outline" size="sm" onClick={goToNextDay}>
            Giorno Successivo
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Schedule View */}
      {!schedule?.is_working_day ? (
        <div className="card p-12 text-center">
          <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 text-lg">Giorno di chiusura</p>
          <p className="text-zinc-500 text-sm mt-1">
            L'officina è chiusa in questa data secondo gli orari configurati
          </p>
        </div>
      ) : (
        <div className="card p-4">
          {/* Legend */}
          <div className="flex items-center gap-6 mb-4 pb-4 border-b border-zinc-700">
            <span className="text-sm text-zinc-400">Ponti:</span>
            {Array.from({ length: schedule?.num_bridges || 1 }, (_, i) => {
              const color = getBridgeColor(i + 1);
              return (
                <div key={i} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${color.bg}`} />
                  <span className="text-sm text-zinc-300">Ponte #{i + 1}</span>
                </div>
              );
            })}
            <div className="flex items-center gap-4 ml-auto text-xs">
              <div className="flex items-center gap-1">
                <div className="w-1 h-4 bg-red-500 rounded" />
                <span className="text-zinc-400">Alta urgenza</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1 h-4 bg-yellow-500 rounded" />
                <span className="text-zinc-400">Media</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1 h-4 bg-green-500 rounded" />
                <span className="text-zinc-400">Bassa</span>
              </div>
            </div>
          </div>

          {/* Time Grid */}
          <div className="flex">
            {/* Time column */}
            <div className="w-16 flex-shrink-0">
              {timeSlots.map((time, idx) => (
                <div 
                  key={time} 
                  className="h-12 flex items-start justify-end pr-2 text-xs text-zinc-500"
                  style={{ marginTop: idx === 0 ? 0 : -6 }}
                >
                  {time.endsWith(":00") ? time : ""}
                </div>
              ))}
            </div>

            {/* Schedule grid - one column per bridge */}
            <div className="flex-1 flex gap-2">
              {Array.from({ length: schedule?.num_bridges || 1 }, (_, bridgeIdx) => {
                const bridgeNum = bridgeIdx + 1;
                const bridgeOrders = (schedule?.work_orders || []).filter(
                  o => o.bridge_number === bridgeNum && o.status !== "cancelled"
                );
                const color = getBridgeColor(bridgeNum);

                return (
                  <div 
                    key={bridgeNum} 
                    className="flex-1 relative bg-zinc-800/30 rounded border border-zinc-700"
                    style={{ minHeight: `${timeSlots.length * 48}px` }}
                  >
                    {/* Header */}
                    <div className={`sticky top-0 p-2 ${color.bg} rounded-t text-center z-10`}>
                      <span className={`text-sm font-medium ${color.text}`}>
                        Ponte #{bridgeNum}
                      </span>
                    </div>

                    {/* Time grid lines */}
                    <div className="absolute inset-0 top-8">
                      {timeSlots.map((time, idx) => (
                        <div 
                          key={time}
                          className={`h-12 border-b ${time.endsWith(":00") ? 'border-zinc-600' : 'border-zinc-700/50'}`}
                        />
                      ))}
                    </div>

                    {/* Work order blocks */}
                    <div className="absolute inset-0 top-8 p-1">
                      {bridgeOrders.map((order) => {
                        const style = calculateBlockStyle(order);
                        const urgencyClass = URGENCY_INDICATOR[order.urgency] || "";

                        return (
                          <div
                            key={order.work_order_id}
                            className={`absolute left-1 right-1 ${color.bg} ${color.border} border rounded p-2 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity ${urgencyClass}`}
                            style={style}
                            title={`#${order.work_order_number} - ${order.customer_name}`}
                          >
                            <div className="text-xs font-bold text-white truncate">
                              #{order.work_order_number}
                            </div>
                            <div className="text-xs text-white/80 truncate flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {order.customer_name}
                            </div>
                            <div className="text-xs text-white/70 truncate flex items-center gap-1">
                              <Car className="w-3 h-3" />
                              {order.vehicle_plate}
                            </div>
                            <div className="text-xs text-white/70 truncate flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {order.scheduled_time_start} - {order.scheduled_time_end}
                            </div>
                            {order.operator_name && (
                              <div className="text-xs text-white/60 truncate mt-1">
                                Op: {order.operator_name}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Summary */}
          <div className="mt-4 pt-4 border-t border-zinc-700">
            <div className="flex items-center justify-between text-sm">
              <div className="text-zinc-400">
                Ordini programmati: <span className="text-white font-medium">{schedule?.work_orders?.length || 0}</span>
              </div>
              <div className="text-zinc-400">
                Orario lavorativo: <span className="text-white font-medium">
                  {schedule?.working_periods?.map(p => `${p[0]}-${p[1]}`).join(", ") || "N/D"}
                </span>
              </div>
              <div className="text-zinc-400">
                Ponti attivi: <span className="text-white font-medium">{schedule?.num_bridges || 1}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {schedule?.is_working_day && schedule?.work_orders?.length === 0 && (
        <div className="card p-8 text-center">
          <Wrench className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">Nessun ordine di lavoro programmato per questo giorno</p>
        </div>
      )}
    </div>
  );
}
